import React, { FC, HTMLProps } from 'react';
import styled, { css } from 'styled-components';
import getPalette from 'utils/getPalette';

type Variant = 'default' | 'disabled';
type IconSide = 'left' | 'right';
type ContainerProps = {
  icon?: React.ReactElement;
  iconSide: IconSide;
  variant?: Variant;
  error?: boolean;
} & React.HTMLProps<HTMLDivElement>;
/* eslint-disable @typescript-eslint/no-unused-vars */
const ContainerComponent: FC<ContainerProps> = ({
  icon,
  iconSide,
  children,
  error,
  ...props
}) => {
  return <div {...props}>{children}</div>;
};

const Container = styled(ContainerComponent)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  stroke: var(--gray-500);
  margin-left: 1px;
  ${({ variant }) =>
    variant === 'disabled'
      ? `
		cursor: not-allowed;
		background-color: var(--gray-50);
	`
      : ''}

  svg {
    position: absolute;
    ${({ iconSide }) => iconSide}: 1rem;
    top: 0;
    height: 100%;
    pointer-events: none;
  }
  input {
    ${({ variant, error }) =>
      error
        ? `
      background-color: var(--gray-50);
      color: var(--error-500);
    `
        : variant === 'disabled'
        ? `
			cursor: default;
			background-color: var(--gray-50);
			color: var(--gray-500);
		`
        : `
			color: var(--gray-700);
			background:white;
		`}
    width: 100%;
    border: 1px solid
      ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-300)')};
    border-radius: 8px;
    padding: 0.8rem 1rem;
    ${({ iconSide, icon }) =>
      `padding-${iconSide}: ${icon ? '2.8rem' : '1rem'};`}
    box-shadow: var(--box-shadow);
    transition: 0.2s;

    &:hover {
      border: 1px solid
        ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-400)')};
    }
    &:focus {
      outline: 1px solid
        ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-700)')};
    }
    &::placeholder {
      color: ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-500)')};
      opacity: 1;
    }

    &:read-only {
      background-color: var(--gray-50);
      color: var(--gray-600);
      cursor: default;
      outline: none;
      border: 1px solid var(--gray-300);
    }
  }
`;

export const Label = styled(({ error, ...props }) => <p {...props} />)`
  font-size: 0.9rem;
  margin-bottom: 0.375rem;
  color: ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-700)')};
`;

type Props = {
  className?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  icon?: React.ReactElement;
  iconSide?: IconSide;
  variant?: Variant;
  label?: string;
  error?: boolean;
} & HTMLProps<HTMLInputElement>;
const Input: FC<Props> = ({
  className,
  onChange,
  icon,
  variant = 'default',
  iconSide = 'left',
  label,
  error,
  type,
  ...props
}) => {
  return (
    <div>
      {label && <Label error={error}>{label}</Label>}
      {type === 'file' ? (
        <FileInput
          {...props}
          onChange={onChange}
          onWheel={(e) => e.currentTarget.blur()}
        />
      ) : (
        <Container
          className={className}
          icon={icon}
          iconSide={iconSide}
          variant={variant}
          error={error}
        >
          <input
            {...props}
            onChange={onChange}
            onWheel={(e) => e.currentTarget.blur()}
            type={type}
          />
          {icon}
        </Container>
      )}
    </div>
  );
};

type ContainerLabelProps = {
  error?: boolean;
} & React.HTMLProps<HTMLLabelElement>;
const ContainerLabelComponent: FC<ContainerLabelProps> = ({
  children,
  error,
  ...props
}) => {
  return <label {...props}>{children}</label>;
};

const FileInputContainer = styled(ContainerLabelComponent)`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 100%;

  span {
    cursor: pointer;
    border-radius: 8px;
    padding: 0.5rem 0.9rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    font-weight: 500;
    box-shadow: var(--box-shadow);
    font-family: 'Inter', sans-serif;
    position: relative;
    /* overflow-x: hidden; */

    ${({ error }) => {
      const palette = getPalette('filled', error ? 'error' : 'primary');
      return css`
        color: ${palette.color};
        stroke: ${palette.color};
        background-color: ${palette.bgColor};
        border: 1px solid ${palette.border};
        &:hover {
          color: ${palette.hover.color};
          stroke: ${palette.hover.color};
          background-color: ${palette.hover.bgColor};
          border: 1px solid ${palette.hover.border};
        }
      `;
    }}
    width: fit-content;
    white-space: nowrap;
  }
  input:focus-visible ~ span {
    outline: 2px solid black;
  }
  input {
    position: absolute;
    width: 0;
    height: 0;
  }
  p {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const FileInput: FC<Props> = ({
  className,
  error,
  onChange,
  value,
  style,
  ...props
}) => {
  return (
    <FileInputContainer className={className} error={error} style={style}>
      <input {...props} onChange={onChange} type="file" />
      <span>Choisir un fichier</span>
      <p>{value}</p>
    </FileInputContainer>
  );
};

export default Input;
